import { t_dataName } from './../../../../../services/apiHandler/basicMachineFunctions/basic-machine-functions.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    DidDataRetrievalService,
    t_DataIdentifier,
    t_DataPoint,
} from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { GatewayConnectionService } from 'src/app/services/apiHandler/gateway-connection/gateway-connection.service';
import { joystickDataConfig, visualizationJoystickConfig } from '../../data/joystick-datanames';
import { DidConfigService } from 'src/app/services/apiHandler/did-config/did-config.service';
import { Subject, interval, takeUntil } from 'rxjs';
@Component({
    selector: 'app-joystick-movements',
    templateUrl: './joystick-movements.component.html',
    styleUrls: ['./joystick-movements.component.scss'],
})
export class JoystickMovementsComponent implements OnInit, OnDestroy {
    public dataQueryList: t_dataName[] = [];
    public queryFrequency: number = 300;
    public loadedAndQueryStarting: boolean = false;
    public pollingFrequency: number;
    private destroy$: Subject<void> = new Subject<void>();
    public joystickDatasets: t_JoystickDataset[] = [];
    private destroyed: boolean = false;

    constructor(
        private gatewayConnectionService: GatewayConnectionService,
        private didConfigService: DidConfigService,
        private dataRetrieval: DidDataRetrievalService
    ) {
        if (this.queryFrequency <= 65) {
            this.pollingFrequency = 50;
        } else {
            this.pollingFrequency = parseFloat((this.queryFrequency * 0.75).toFixed(0));
        }
    }

    ngOnInit(): void {
        this.setupServices().then(() => {
            if(!this.destroyed) {
                this.dataRetrieval.initService();
                this.initDataQueryList();
                this.postDataQueryListToBackendDidList().then(() => {
                    if(this.destroyed){
                        this.ngOnDestroy();
                    }
                    this.dataRetrieval.startDataQuery();
                    this.loadedAndQueryStarting = true;
                    this.setupJoystickDatasets();
                    this.startPeriodicPolling();
                });
            }
        });
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.destroy$.next();
        this.destroy$.complete();
        this.dataRetrieval.stopSendQueryAliveState();
        this.dataRetrieval.stopDataQuery();
        this.dataRetrieval.cleanDataPoints();
        this.dataQueryList.forEach(data => {
            this.didConfigService.removeDidFromQueryList({ecu_id: data.identifier!.ecu_id, data_id: data.identifier!.data_id}).subscribe();
        });
    }

    private setupServices(): Promise<void> {
        return new Promise((resolve) => {
            this.gatewayConnectionService.establishConnectionToGateway().then(
                () => {
                    resolve();
                    // Resolve, Connection established, Load DidMapping
                    /*this.didConfigService.loadEcuList().then(() => {
                        this.didConfigService.ecuList!.forEach((ecu) => {
                            this.didConfigService.setEcuDidMapping(ecu.ecu_id).then(() => {
                                resolve();
                            });
                        });
                    });*/
                },
                () => {
                    // Rejected, no connection possible
                    console.error('ERROR');
                }
            );
        });
    }

    private initDataQueryList(): void {
        this.dataQueryList = this.getDataIdentifiersToDataNamesFromConfig(); 
        if(this.destroyed) {
            this.ngOnDestroy();
        }
    }

    private getDataIdentifiersToDataNamesFromConfig(): t_dataName[] {
        // For each name in joystick visualization config get dataidentifier and get did information
        // Store it as t_DataName and push it in array -> Return Array
        let dataNames: t_dataName[] = [];
        visualizationJoystickConfig.dataNames.forEach((_data) => {
            let identifier: t_DataIdentifier = this.didConfigService.getDataIdentifierOfDataName(_data.dataName);
            if(identifier.ecu_id != undefined) {
                let info = this.didConfigService.getDidInformation(identifier);

                let dataName: t_dataName = {
                    name: _data.dataName,
                    identifier: identifier,
                    didInformation: info,
                };
                dataNames.push(dataName);
            }
        });

        return dataNames;
    }

    private postDataQueryListToBackendDidList(): Promise<void> {
        return new Promise((resolve) => {
            this.didConfigService
                .postNewDidQueryList(
                    /* Create an array of identifiers from datanames and filter undefined values */
                    this.dataQueryList.map((item) => item.identifier).filter((identifier) => identifier !== undefined) as t_DataIdentifier[]
                )
                .subscribe({
                    complete: () => {
                        resolve();
                    },
                });
        });
    }

    private setupJoystickDatasets(): void {
        this.joystickDatasets = joystickDataConfig;

        this.joystickDatasets.forEach((joystick) => {
            joystick.forwardBackward.dataname.identifier = this.didConfigService.getDataIdentifierOfDataName(joystick.forwardBackward.dataname.name);
            joystick.leftRight.dataname.identifier = this.didConfigService.getDataIdentifierOfDataName(joystick.leftRight.dataname.name);
        });
        this.joystickDatasets = this.joystickDatasets
            .map((dataPoint) => dataPoint)
            .filter((dataPoint) => dataPoint.forwardBackward.dataname.identifier?.ecu_id !== undefined);
        this.joystickDatasets = this.joystickDatasets
            .map((dataPoint) => dataPoint)
            .filter((dataPoint) => dataPoint.leftRight.dataname.identifier?.ecu_id !== undefined);
    }

    private startPeriodicPolling() {
        interval(this.pollingFrequency)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.getDataFromBackend();
            });
    }

    private getDataFromBackend() {
        // Get all Dids Values, because did list was Posted, so no other dids are present in the list
        this.dataRetrieval.getAllDidsValues().subscribe((response) => {
            if (response.body != null) {
                this.processRawData(response.body);
            }
        });
    }

    private processRawData(rawData: t_DataPoint[]) {
        this.joystickDatasets.forEach((joystick) => {
            // Retrieve Forward Backward Value
            let forwardBackwardIndex: number = rawData.findIndex((datapoint) => {
                return (
                    datapoint.identifier.ecu_id == joystick.forwardBackward.dataname.identifier?.ecu_id &&
                    datapoint.identifier.data_id == joystick.forwardBackward.dataname.identifier?.data_id
                );
            });

            if (rawData[forwardBackwardIndex].data != undefined) {
                let normalizedValue: number = this.normalizedValue(
                    rawData[forwardBackwardIndex].data!.value[0],
                    this.dataQueryList[forwardBackwardIndex].didInformation!.conversionFactor,
                    this.dataQueryList[forwardBackwardIndex].didInformation!.conversionOffset
                );
                joystick.forwardBackward.value = normalizedValue;
            }

            // Retrieve Left Right Value
            let leftRightIndex: number = rawData.findIndex((datapoint) => {
                return (
                    datapoint.identifier.ecu_id == joystick.leftRight.dataname.identifier?.ecu_id &&
                    datapoint.identifier.data_id == joystick.leftRight.dataname.identifier?.data_id
                );
            });

            if (rawData[leftRightIndex].data != undefined) {
                let normalizedValue: number = this.normalizedValue(
                    rawData[leftRightIndex].data!.value[0],
                    this.dataQueryList[leftRightIndex].didInformation!.conversionFactor,
                    this.dataQueryList[leftRightIndex].didInformation!.conversionOffset
                );
                joystick.leftRight.value = normalizedValue;
            }
        });
    }

    private normalizedValue(value: number, factor: number, offset: number) {
        if(factor == 0) {
            factor = 1;
        }
        let res: number = (value * factor) + offset;
        res = parseFloat(res.toFixed(1));
        return res;
    }
}

export type t_JoystickDataset = {
    joystickName: string;
    forwardBackward: {
        dataname: t_dataName;
        value: number;
    };
    leftRight: {
        dataname: t_dataName;
        value?: number;
    };
};
